import i18next from 'i18next';
import { call, put } from 'redux-saga/effects';
import { v4 as generateId } from 'uuid';

import {
  checkAuth,
  checkPasswordResetToken,
  checkSsoAzureConfig,
  checkSsoJwtConfig,
  checkSsoSamlConfig,
  clearMfaContext,
  createNewPassword,
  forgotPassword,
  login,
  resetPassword,
  setAuthLoading,
  setCheckPasswordResetTokenLoading,
  setLoginLoading,
  setMfaContext,
  signup,
} from 'actions/app.actions';
import { enqueueSnackbar } from 'actions/notifications.actions';
import ROUTES from 'constants/routes';
import { getProvidedByServerErrors } from 'core/utils/api.helpers';
import { ActionType } from 'interfaces';
import { AuthAPI } from 'services/auth.api';
import historySingleton from 'services/history';

export function* loginSaga({
  payload: { username, password, callback },
}: ActionType): any {
  try {
    yield put(setLoginLoading(true));
    const { data } = yield call(AuthAPI.login, { username, password });

    if (data?.code === 423) {
      // usecase spécifique pour le code 423 qui peut être retourné par le backend en cas de blocage temporaire du compte suite à trop de tentatives de connexion infructueuses sur le formulaire de MFA (voir le composant MfaCodeVerifyForm)
      yield put(setLoginLoading(false));
      const key = yield call(generateId);
      yield put(
        enqueueSnackbar({
          message: i18next.t('auth.login.error423'),
          options: {
            key,
            variant: 'error',
          },
        }),
      );
      return;
    }

    const mfaCodeRequired = data['2fa_required'];
    const tempToken = data.temp_token;
    // Activer MFA si la variable d'env REACT_APP_USE_MFA !== 'true'
    if (mfaCodeRequired && tempToken) {
      // 2FA required - redirect to MFA page without setting token
      yield put(setLoginLoading(false));
      yield put(setMfaContext(tempToken));
      // Propager le returnPath si présent dans l'URL
      const searchParams = new URLSearchParams(window.location.search);
      const returnPath = searchParams.get('returnPath');
      if (returnPath) {
        const mfaUrl = `${
          ROUTES.MFA_CODE_VERIFY
        }?returnPath=${encodeURIComponent(returnPath)}`;
        historySingleton.push(mfaUrl);
      } else {
        historySingleton.push(ROUTES.MFA_CODE_VERIFY);
      }
      return;
    }

    // Normal login flow (no MFA): ensure no stale MFA context remains
    yield put(clearMfaContext());
    yield put(login.success());
    yield put(checkAuth.request());
    if (callback) {
      // Le callback recharge la page : on garde l'écran de chargement jusqu'au déchargement,
      // sinon le formulaire se remonte et reste re-soumettable pendant le rechargement.
      callback();

      return;
    }
    yield put(setLoginLoading(false));
  } catch (error) {
    yield put(setLoginLoading(false));
    // Clear MFA context on any login error to avoid keeping stale email
    yield put(clearMfaContext());
    const providedByServerErrors = getProvidedByServerErrors(error);
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: providedByServerErrors || i18next.t('auth.login.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
    yield put(login.failure({ error, message: 'error.fail' }));
  }
}

export function* signUpSaga({ payload: { data, onSignUp } }: ActionType): any {
  try {
    yield put(setLoginLoading(true));

    yield call(AuthAPI.signUp, data);
    yield put(signup.success());
    yield call(onSignUp);
  } catch (error) {
    yield put(setLoginLoading(false));
    const providedByServerErrors = getProvidedByServerErrors(error);
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: providedByServerErrors || i18next.t('auth.signup.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
    yield put(signup.failure({ error, message: 'error.fail' }));
  }
}

export function* forgotPasswordSaga({ payload: { email } }: ActionType): any {
  try {
    yield put(setLoginLoading(true));
    yield call(AuthAPI.forgotPassword, email);
    yield put(forgotPassword.success());
    yield put(setLoginLoading(false));
  } catch (error) {
    yield put(setLoginLoading(false));
    yield put(forgotPassword.failure({ error, message: 'error.fail' }));
  } finally {
    const key = yield call(generateId);

    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.reset_password.email_sent'),
        options: {
          key,
          variant: 'info',
        },
      }),
    );
  }
}

export function* resetPasswordSaga({
  payload: { history, ...data },
}: ActionType): any {
  try {
    yield call(AuthAPI.resetPassword, data);
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.reset_password.success'),
        options: {
          key,
          variant: 'success',
        },
      }),
    );
    yield call(history.push, ROUTES.LOGIN);
  } catch (error) {
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.signup.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
    yield put(resetPassword.failure({ error, message: 'error.fail' }));
  }
}

export function* createNewPasswordSaga({
  payload: { onCreate, ...data },
}: ActionType): any {
  try {
    yield put(setLoginLoading(true));
    yield call(AuthAPI.createNewPassword, data);
    yield put(createNewPassword.success());
    yield call(onCreate);
  } catch (error) {
    yield put(setLoginLoading(false));
    yield put(createNewPassword.failure({ error, message: 'error.fail' }));
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.createNewPassword.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
  }
}

export function* checkAuthenticationSaga(): any {
  try {
    yield put(setAuthLoading(true));
    const { data: userInfos } = yield call(AuthAPI.checkAuth);
    yield put(checkAuth.success(userInfos));
    yield put(setAuthLoading(false));
  } catch (e) {
    yield put(setAuthLoading(false));
  }
}

export function* checkSsoAzureConfigSaga(): any {
  try {
    const { data: isSsoAzureConfigured } = yield call(
      AuthAPI.checkSsoAzureConfig,
    );
    yield put(checkSsoAzureConfig.success(isSsoAzureConfigured));
  } catch (error) {
    yield put(checkSsoAzureConfig.failure(error));
  }
}

export function* checkSsoJwtConfigSaga(): any {
  try {
    const { data: isSsoJwtConfigured } = yield call(AuthAPI.checkSsoJwtConfig);
    yield put(checkSsoJwtConfig.success(isSsoJwtConfigured));
  } catch (error) {
    yield put(checkSsoJwtConfig.failure(error));
  }
}

export function* checkSsoSamlConfigSaga(): any {
  try {
    const { data: isSsoSamlConfigured } = yield call(
      AuthAPI.checkSsoSamlConfig,
    );
    yield put(checkSsoSamlConfig.success(isSsoSamlConfigured));
  } catch (error) {
    yield put(checkSsoSamlConfig.failure(error));
  }
}

export function* checkPasswordResetTokenSaga({
  payload: { token },
}: ActionType): any {
  try {
    yield put(setCheckPasswordResetTokenLoading(true));
    const {
      data: { isValid, email },
    } = yield call(AuthAPI.checkPasswordResetToken, token);
    yield put(checkPasswordResetToken.success({ isValid, email }));
  } catch (error) {
    yield put(checkPasswordResetToken.failure(error));
  } finally {
    yield put(setCheckPasswordResetTokenLoading(false));
  }
}
